import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Loader2 } from 'lucide-react';
import { operationalCostsAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { AnnualBarChart, MONTH_NAMES, isFutureMonth } from './AnnualBarChart';

interface MonthSummary {
  month: number;
  total: number;
  count: number;
  recurring: number;
  single: number;
  byCategory: Record<string, number>;
}

interface AnnualSummary {
  year: number;
  months: MonthSummary[];
  totals: Omit<MonthSummary, 'month'>;
}

const CATEGORIES: { key: string; label: string }[] = [
  { key: 'TOOLS', label: 'Ferramentas' },
  { key: 'EVENTS', label: 'Eventos' },
  { key: 'OFFICE', label: 'Escritório' },
  { key: 'SERVICES', label: 'Serviços' },
  { key: 'MARKETING', label: 'Marketing' },
  { key: 'REFUNDS', label: 'Prejuízos e Reembolsos' },
  { key: 'TAXES', label: 'Impostos e Encargos' },
];

function Stat({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
  return (
    <Card>
      <CardContent className="py-5">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <p className="mt-2 text-2xl font-bold">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

const money = (value: number) => (value ? formatCurrency(value) : '—');

/**
 * Despesas do ano, mês a mês: total, categorias e recorrentes × únicas.
 * Meses futuros (lançamentos recorrentes já gerados) aparecem como previstos.
 */
export function OperationalCostsAnnualView({ year, onSelectMonth }: { year: number; onSelectMonth: (month: number) => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['operational-costs', 'annual', year],
    queryFn: async () => (await operationalCostsAPI.getAnnualSummary(year)).data.data as AnnualSummary,
  });

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const realized = data.months.filter((m) => !isFutureMonth(year, m.month));
  const forecast = data.months.filter((m) => isFutureMonth(year, m.month));
  const realizedTotal = realized.reduce((sum, m) => sum + m.total, 0);
  const realizedRecurring = realized.reduce((sum, m) => sum + m.recurring, 0);
  const forecastTotal = forecast.reduce((sum, m) => sum + m.total, 0);
  const monthsWithCosts = realized.filter((m) => m.total > 0).length;
  const peak = realized.reduce<MonthSummary | null>((best, m) => (!best || m.total > best.total ? m : best), null);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat
          title={forecast.length ? `Gasto até ${MONTH_NAMES[realized.length - 1]?.toLowerCase() ?? ''}` : `Total de ${year}`}
          value={formatCurrency(realizedTotal)}
          subtitle={`${realized.reduce((s, m) => s + m.count, 0)} lançamentos`}
        />
        <Stat
          title="Média mensal"
          value={formatCurrency(monthsWithCosts ? realizedTotal / monthsWithCosts : 0)}
          subtitle={`${monthsWithCosts} ${monthsWithCosts === 1 ? 'mês' : 'meses'} com despesas`}
        />
        <Stat
          title="Recorrentes"
          value={formatCurrency(realizedRecurring)}
          subtitle={`${realizedTotal ? Math.round((realizedRecurring / realizedTotal) * 100) : 0}% do gasto · únicas ${formatCurrency(realizedTotal - realizedRecurring)}`}
        />
        <Stat
          title={forecast.length ? 'Previsto até dezembro' : 'Maior mês'}
          value={forecast.length ? formatCurrency(forecastTotal) : peak ? formatCurrency(peak.total) : '—'}
          subtitle={forecast.length ? 'Recorrências já geradas dos próximos meses' : peak ? MONTH_NAMES[peak.month - 1] : ''}
        />
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold">Despesas por mês</h2>
            <p className="text-sm text-muted-foreground">Barras claras são meses previstos. Clique em um mês para ver os lançamentos.</p>
          </div>
          <AnnualBarChart
            data={data.months.map((m) => ({ month: m.month, value: m.total, isForecast: isFutureMonth(year, m.month) }))}
            color="#ef4444"
            onSelectMonth={onSelectMonth}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-3 px-3 text-left font-medium">Mês</th>
                  {CATEGORIES.map((cat) => (
                    <th key={cat.key} className="py-3 px-3 text-right font-medium whitespace-nowrap">{cat.label}</th>
                  ))}
                  <th className="py-3 px-3 text-right font-medium">Recorrentes</th>
                  <th className="py-3 px-3 text-right font-medium">Únicas</th>
                  <th className="py-3 px-3 text-right font-semibold text-foreground">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => {
                  const future = isFutureMonth(year, m.month);
                  return (
                    <tr
                      key={m.month}
                      onClick={() => onSelectMonth(m.month)}
                      className={`border-b cursor-pointer hover:bg-accent/50 transition-colors ${future ? 'text-muted-foreground' : ''}`}
                    >
                      <td className="py-2.5 px-3 font-medium whitespace-nowrap">
                        {MONTH_NAMES[m.month - 1]}
                        {future && <span className="ml-2 text-xs font-normal">(previsto)</span>}
                      </td>
                      {CATEGORIES.map((cat) => (
                        <td key={cat.key} className="py-2.5 px-3 text-right tabular-nums">{money(m.byCategory[cat.key] || 0)}</td>
                      ))}
                      <td className="py-2.5 px-3 text-right tabular-nums">{money(m.recurring)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{money(m.single)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums font-semibold">{money(m.total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-3 px-3">Total {year}{forecast.length ? ' (com previstos)' : ''}</td>
                  {CATEGORIES.map((cat) => (
                    <td key={cat.key} className="py-3 px-3 text-right tabular-nums">{money(data.totals.byCategory[cat.key] || 0)}</td>
                  ))}
                  <td className="py-3 px-3 text-right tabular-nums">{money(data.totals.recurring)}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{money(data.totals.single)}</td>
                  <td className="py-3 px-3 text-right tabular-nums">{money(data.totals.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
